/**
 * The racetrack. 16 desert paving-stone tiles in a row, a dice pyramid at the
 * start, a checkered finish line + flag at the end, and a "finished" tray for
 * camels that have crossed. Camels stack upward on each tile with an offset
 * and a shared drop shadow so a tall stack reads as camels riding each other.
 *
 * Horizontally scrollable when the track overflows (phones / narrow TVs).
 */

import type { CamelColor, CamelUp } from '@mdev/common'
import { CU, CU_HEADING } from '../theme'
import { CamelGlyph } from './CamelGlyph'

type CamelUpState = CamelUp.CamelUpState

interface Props {
  state: CamelUpState
  /** Pixel size for each camel glyph. */
  camelSize?: number
}

export function BoardStrip(props: Props) {
  const { state, camelSize = 34 } = props
  const tileW = camelSize * 1.95
  const tileMinH = camelSize * 3.4
  const spaces: number[] = []
  for (let s = 1; s <= 16; s++) spaces.push(s)

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'stretch',
        gap: 6,
        overflowX: 'auto',
        padding: '14px 12px',
        borderRadius: 18,
        background:
          'linear-gradient(180deg, rgba(120,72,20,0.10), rgba(120,72,20,0.02))',
        border: `1px solid ${CU.borderSoft}`,
        boxShadow: `inset 0 2px 10px ${CU.trackGroove}`,
      }}
    >
      <Pyramid h={tileMinH} dice={state.pyramidDice.length} />

      {spaces.map((s) => {
        const stack = (state.spaces[s] as CamelColor[]) ?? []
        const alt = s % 2 === 0
        return (
          <div
            key={s}
            style={{
              width: tileW,
              minHeight: tileMinH,
              flex: '0 0 auto',
              borderRadius: 10,
              background: `linear-gradient(180deg, ${alt ? CU.trackBgAlt : CU.trackBg}, ${CU.trackBg})`,
              border: `1px solid ${CU.borderSoft}`,
              boxShadow: `inset 0 3px 6px ${CU.trackGroove}, inset 0 -2px 0 rgba(255,255,255,0.18)`,
              position: 'relative',
              display: 'flex',
              flexDirection: 'column-reverse',
              alignItems: 'center',
              justifyContent: 'flex-start',
              paddingBottom: 22,
              paddingTop: 8,
            }}
          >
            {/* Camel stack (bottom→top via column-reverse) */}
            {stack.map((c, i) => (
              <div
                key={`${c}-${i}`}
                style={{
                  marginTop: i === 0 ? 0 : -camelSize * 0.5,
                  filter: i === stack.length - 1 ? 'drop-shadow(0 3px 2px rgba(60,36,10,0.3))' : 'none',
                  zIndex: i + 1,
                }}
              >
                <CamelGlyph color={c} size={camelSize} noShadow />
              </div>
            ))}

            {/* Tile number badge */}
            <span
              style={{
                position: 'absolute',
                bottom: 4,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: CU.panelBg,
                border: `1.5px solid ${CU.brass}`,
                color: CU.brass,
                fontFamily: CU_HEADING,
                fontWeight: 600,
                fontSize: 11,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 1px 2px rgba(60,36,10,0.3)',
              }}
            >
              {s}
            </span>
          </div>
        )
      })}

      <FinishTray state={state} camelSize={camelSize} h={tileMinH} />
    </div>
  )
}

/** Stone pyramid at the start; shows how many dice remain inside. */
function Pyramid({ h, dice }: { h: number; dice: number }) {
  const w = h * 0.72
  return (
    <div
      style={{
        width: w,
        minHeight: h,
        flex: '0 0 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-end',
        paddingBottom: 2,
      }}
    >
      <svg width={w} height={h * 0.82} viewBox="0 0 100 110" style={{ overflow: 'visible' }}>
        <defs>
          <linearGradient id="cu-pyr" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#f0cf8f" />
            <stop offset="100%" stopColor="#c9912f" />
          </linearGradient>
        </defs>
        {/* shaded right face */}
        <path d="M50 6 L96 104 H50 Z" fill="#b07d24" />
        {/* lit left face */}
        <path d="M50 6 L4 104 H50 Z" fill="url(#cu-pyr)" />
        {/* brick courses */}
        <g stroke="rgba(120,72,20,0.35)" strokeWidth="1">
          <line x1="33" y1="40" x2="67" y2="40" />
          <line x1="22" y1="63" x2="78" y2="63" />
          <line x1="12" y1="86" x2="88" y2="86" />
        </g>
      </svg>
      <span
        style={{
          fontFamily: CU_HEADING,
          fontWeight: 600,
          fontSize: 10,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: CU.inkSoft,
        }}
      >
        {dice} dice
      </span>
    </div>
  )
}

/** Checkered finish line + flag, then the crossed-camels tray. */
function FinishTray({
  state,
  camelSize,
  h,
}: {
  state: CamelUpState
  camelSize: number
  h: number
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'stretch', gap: 8, flex: '0 0 auto' }}>
      {/* Finish line */}
      <div
        style={{
          width: 16,
          minHeight: h,
          borderRadius: 4,
          backgroundImage:
            'linear-gradient(45deg, #2a1d0e 25%, transparent 25%), linear-gradient(-45deg, #2a1d0e 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #2a1d0e 75%), linear-gradient(-45deg, transparent 75%, #2a1d0e 75%)',
          backgroundColor: '#fbf1dc',
          backgroundSize: '12px 12px',
          backgroundPosition: '0 0, 0 6px, 6px -6px, -6px 0',
          position: 'relative',
          border: `1px solid ${CU.border}`,
        }}
      >
        <svg width="26" height="22" viewBox="0 0 26 22" style={{ position: 'absolute', top: -16, left: 2 }}>
          <line x1="3" y1="0" x2="3" y2="22" stroke={CU.brass} strokeWidth="2" />
          <path d="M3 1 L24 1 L19 6 L24 11 L3 11 Z" fill={CU.accent} />
        </svg>
      </div>

      {state.finished.length > 0 && (
        <div
          style={{
            minWidth: camelSize * 2.2,
            minHeight: h,
            borderRadius: 10,
            background: CU.surfaceSolid,
            border: `1.5px solid ${CU.brass}`,
            padding: 6,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            boxShadow: CU.shadowSoft,
          }}
        >
          {state.finished.map((c, i) => (
            <div key={`${c}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  fontFamily: CU_HEADING,
                  fontWeight: 600,
                  fontSize: 12,
                  color: CU.brass,
                  width: 14,
                }}
              >
                {i + 1}
              </span>
              <CamelGlyph color={c} size={camelSize * 0.8} noShadow />
            </div>
          ))}
          <span
            style={{
              marginTop: 'auto',
              fontFamily: CU_HEADING,
              fontSize: 9,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: CU.inkSoft,
              textAlign: 'center',
            }}
          >
            Finished
          </span>
        </div>
      )}
    </div>
  )
}
